import React, { Fragment } from 'react'
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Dialog, Transition } from '@headlessui/react'

import {
    ArrowRightOnRectangleIcon,
    Bars3Icon,
    ChartBarIcon,
    ChatBubbleBottomCenterIcon,
    ClipboardDocumentIcon,
    ShoppingCartIcon,
    UserCircleIcon,
    WrenchIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline'
import { send, useFibaMsgBox, useLoading } from '../components'
import { PhotoIcon } from '@heroicons/react/24/solid'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const PrivateRoute = () => {
    const { isAuth, auth } = useAuth()
    const [sidebarOpen, setSidebarOpen] = React.useState(false)
    const location = useLocation();
    const navigate = useNavigate();
    const { showLoading } = useLoading()
    const { error } = useFibaMsgBox()

    const navigation = [
        // {
        //     name: 'Үндсэн цэс бүртгэх',
        //     href: '/module', icon: HomeIcon,
        //     current: location.pathname === '/module'
        // },
        {
            name: 'Захиалга',
            href: '/order', icon: ShoppingCartIcon,
            current: location.pathname === '/order'
        },
        {
            name: 'Бүтээгдэхүүн',
            href: '/product', icon: Bars3Icon,
            current: location.pathname === '/product'
        },
        {
            name: 'Зөвлөмж',
            href: '/advice', icon: ChatBubbleBottomCenterIcon,
            current: location.pathname === '/advice'
        },
        {
            name: 'Зураг',
            href: '/carousel', icon: PhotoIcon,
            current: location.pathname === '/carousel'
        },
        {
            name: 'Хэрэглэгчийн сэтгэгдэл',
            href: '/usercomment', icon: ChartBarIcon,
            current: location.pathname === '/usercomment'
        },
        {
            name: 'Тогтмолын бүртгэл',
            href: '/consts', icon: ClipboardDocumentIcon,
            current: location.pathname === '/consts'
        },
        {
            name: 'Эрхийн тохиргоо',
            href: '/roles', icon: WrenchIcon,
            current: location.pathname === '/roles'
        },
        // {
        //     name: 'Logo бүртгэх',
        //     href: '/orglogo', icon: LightBulbIcon,
        //     current: location.pathname === '/orglogo'
        // },
    ]

    const logout = async () => {
        const res = await send('lo010101', {}, showLoading);
        if (res.status === 'error') {
            error(res.message)
        } else {
            navigate('/login')
        }
    }

    return isAuth() ? <>
        <div>
            <Transition.Root show={sidebarOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-900/80" />
                    </Transition.Child>

                    <div className="fixed inset-0 flex">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel className="relative flex flex-1 w-full max-w-xs mr-16">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-in-out duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="ease-in-out duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div className="absolute top-0 flex justify-center w-16 pt-5 left-full">
                                        <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                                            <span className="sr-only">Close sidebar</span>
                                            <XMarkIcon className="w-6 h-6 text-white" aria-hidden="true" />
                                        </button>
                                    </div>
                                </Transition.Child>
                                {/* Sidebar component, swap this element with another sidebar if you like */}
                                <div className="flex flex-col px-6 pb-2 overflow-y-auto bg-white grow gap-y-5">
                                    <div className="flex items-center h-16 shrink-0">
                                        <img
                                            className="w-auto h-10"
                                            src="/logos/logoblack.png"
                                            alt="Your Company"
                                        />
                                    </div>
                                    <nav className="flex flex-col flex-1">
                                        <ul className="flex flex-col flex-1 gap-y-7">
                                            <li>
                                                <ul className="-mx-2 space-y-1">
                                                    {navigation.map((item) => (
                                                        <li key={item.name}>
                                                            <Link
                                                                to={item.href}
                                                                className={classNames(
                                                                    item.current
                                                                        ? 'bg-gray-50 text-indigo-600'
                                                                        : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50',
                                                                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                                                                )}
                                                            >
                                                                <item.icon
                                                                    className={classNames(
                                                                        item.current ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                                                                        'h-6 w-6 shrink-0'
                                                                    )}
                                                                    aria-hidden="true"
                                                                />
                                                                {item.name}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Static sidebar for desktop */}
            <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex flex-col px-6 overflow-y-auto bg-white border-r border-gray-200 grow gap-y-5">
                    <div className="flex items-center h-20 shrink-0">
                        <img
                            className="w-auto h-16 mx-auto"
                            src="/logos/logoblack.png"
                            alt="Your Company"
                        />
                    </div>
                    <nav className="flex flex-col flex-1">
                        <ul className="flex flex-col flex-1 gap-y-7">
                            <li>
                                <ul className="-mx-2 space-y-1">
                                    {navigation.map((item) => (
                                        <li key={item.name}>
                                            <Link
                                                to={item.href}
                                                className={classNames(
                                                    item.current
                                                        ? 'bg-gray-50 text-indigo-600'
                                                        : 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50',
                                                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                                                )}
                                            >
                                                <item.icon
                                                    className={classNames(
                                                        item.current ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                                                        'h-6 w-6 shrink-0'
                                                    )}
                                                    aria-hidden="true"
                                                />
                                                {item.name}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                            <li className="mt-auto -mx-6">
                                <Link
                                    to="/profile"
                                    className="flex items-center px-6 py-3 text-sm font-semibold leading-6 text-gray-900 gap-x-4 hover:bg-gray-50"
                                >
                                    <UserCircleIcon className="w-8 h-8 rounded-full bg-gray-50" />
                                    <span className="sr-only">Your profile</span>
                                    <span aria-hidden="true">{auth.user.name}</span>
                                </Link>
                            </li>
                            <li>
                                <div
                                    onClick={logout}
                                    className={classNames(
                                        'text-gray-700 hover:text-indigo-600 hover:bg-gray-50',
                                        'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold cursor-pointer'
                                    )}
                                >
                                    <ArrowRightOnRectangleIcon
                                        className={classNames(
                                            'text-gray-400 group-hover:text-indigo-600',
                                            'h-6 w-6 shrink-0'
                                        )} />
                                    <span aria-hidden="true">Гарах</span>
                                </div>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            <div className="sticky top-0 z-40 flex items-center px-4 py-4 bg-white shadow-sm gap-x-6 sm:px-6 lg:hidden">
                <button type="button" className="-m-2.5 p-2.5 text-gray-700 lg:hidden" onClick={() => setSidebarOpen(true)}>
                    <span className="sr-only">Open sidebar</span>
                    <Bars3Icon className="w-6 h-6" aria-hidden="true" />
                </button>
                <div className="flex-1 text-sm font-semibold leading-6 text-gray-900">Dashboard</div>
                <Link to="/profile">
                    <span className="sr-only">Your profile</span>
                    <UserCircleIcon className="w-8 h-8 rounded-full bg-gray-50" />
                </Link>
            </div>

            <main className="py-10 lg:pl-72">
                <div className="px-4 sm:px-6 lg:px-8">
                    <Outlet />
                </div>
            </main>
        </div>
    </> : <Navigate to="/login" />;
}

export default PrivateRoute
